/**
 * One way to show a server timestamp, in the viewer's timezone, named.
 *
 * Owner ask 2026-08-30: "for every lifemap item, or a place or a person or
 * identity, we should see the created date-time in the user's current timezone
 * in the browser with the tz mentioned + the same for any updates recorded."
 *
 * Three jobs, and each was broken somewhere before this existed:
 *
 *   1. PARSE CORRECTLY. Server timestamps in the JSONB body are naive UTC, and
 *      `new Date(naive)` reads them as local — every Created:/Updated: line in
 *      the app was off by the viewer's UTC offset, which near midnight UTC is
 *      off by a whole DAY. `parseServerUtc` handles both shapes the backend
 *      emits.
 *   2. SHOW THE RIGHT ZONE. useUserTimezone resolves the user's real zone
 *      (falling back to the browser's), and it no longer lets the profile's
 *      "UTC" placeholder clobber a detected one.
 *   3. NAME THE ZONE. "3:40 PM" is a different moment to every reader. An
 *      audit line that does not say PDT is not auditable.
 *
 * The exact UTC instant is always in the title attribute, so anyone
 * reconciling the UI against the database has the raw value one hover away
 * without the page having to show two timestamps.
 */
import { useUserTimezone } from '../../hooks/useUserTimezone'
import { parseServerUtc } from '../../utils/datetime'

export interface TimestampProps {
  /** ISO string from the server. Naive or offset-aware; both are handled. */
  value?: string | null
  /** `datetime` names the zone; `date` is the compact form for dense lists;
   *  `time` is the clock with its zone ("9:00 AM PDT") for an instant the
   *  sentence has already placed on a day ("today at …", "done …"). */
  mode?: 'datetime' | 'date' | 'time'
  /** Shown when the value is missing or unparseable. */
  fallback?: string
  className?: string
  /** Optional leading label, e.g. "Created". Rendered muted. */
  label?: string
}

export function Timestamp({
  value, mode = 'datetime', fallback = '—', className = '', label,
}: TimestampProps) {
  const { formatDate, formatDateTime, formatTime, timezone } = useUserTimezone()
  const d = parseServerUtc(value)

  if (!d) {
    return (
      <span className={className}>
        {label && <span className="text-muted">{label} </span>}
        {fallback}
      </span>
    )
  }

  // `date` mode deliberately drops the time AND the zone label: in a dense
  // list they are noise, and the exact instant is still on the title. It is
  // correct here in a way it was not before, because the parse is right.
  const text = mode === 'date' ? formatDate(d) : mode === 'time' ? formatTime(d, { timeZoneName: 'short' }) : formatDateTime(d)

  return (
    <span
      className={className}
      title={`${d.toISOString()} (UTC) · shown in ${timezone}`}
    >
      {label && <span className="text-muted">{label} </span>}
      <time dateTime={d.toISOString()}>{text}</time>
    </span>
  )
}

export default Timestamp
